import { Link } from "react-router-dom";
import { useState } from "react";

export default function GetStarted() {
  const [currentStep, setCurrentStep] = useState(1);
  const [userType, setUserType] = useState(null);

  const steps = [
    {
      number: 1,
      title: "Choose Your Role",
      description: "Are you a teacher or a student?",
      icon: "🎭"
    },
    {
      number: 2, 
      title: "Create Account",
      description: "Sign up for your free trial",
      icon: "📝"
    },
    {
      number: 3,
      title: "Quick Setup", 
      description: "Configure your preferences",
      icon: "⚙️"
    },
    {
      number: 4,
      title: "Start Testing",
      description: "Create your first exam or take a test",
      icon: "🚀"
    }
  ];

  const teacherSteps = [
    {
      step: 1,
      title: "🚀 Start Your Free Trial",
      description: "Create your teacher account instantly - no credit card required!",
      action: "Sign Up as Teacher",
      link: "/teacher-register",
      time: "2 minutes",
      details: [
        "Enter your name and email",
        "Choose a secure password", 
        "Verify your email address",
        "Access your dashboard immediately"
      ]
    },
    {
      step: 2,
      title: "📚 Create Your First Exam",
      description: "Use our AI-powered exam builder to create a comprehensive test in minutes",
      action: "Try AI Exam Builder",
      link: "/teacher/ai-builder",
      time: "5 minutes",
      details: [
        "Enter your exam topic (e.g., 'Algebra', 'World War II')",
        "Select difficulty level and question count",
        "Review AI-generated questions",
        "Customize and save your exam"
      ]
    },
    {
      step: 3,
      title: "👥 Add Your Students",
      description: "Import students or let them self-register with your class code",
      action: "Manage Students",
      link: "/student-manager",
      time: "3 minutes", 
      details: [
        "Upload CSV file with student names",
        "Or manually add students one by one",
        "Share your unique class code",
        "Students can self-register instantly"
      ]
    },
    {
      step: 4,
      title: "📊 Assign & Monitor",
      description: "Assign exams to students and monitor them in real-time",
      action: "Create Assignment",
      link: "/teacher/assignments",
      time: "2 minutes",
      details: [
        "Select exam and students",
        "Set start/end times",
        "Enable real-time monitoring",
        "Watch students take exam live"
      ]
    }
  ];

  const studentSteps = [
    {
      step: 1,
      title: "📝 Get Your Account Info",
      description: "Ask your teacher for your login credentials",
      action: "Contact Your Teacher",
      link: "/contact",
      time: "1 minute",
      details: [
        "Ask your teacher for your username and password",
        "Teachers create student accounts for security",
        "You'll receive your login credentials directly",
        "Use these credentials to login securely"
      ]
    },
    {
      step: 2,
      title: "🔐 Student Login",
      description: "Use your credentials to access assigned exams",
      action: "Login to Dashboard",
      link: "/student-login",
      time: "30 seconds",
      details: [
        "Enter your email and password",
        "Access your student dashboard",
        "View all assigned exams",
        "Check exam schedules"
      ]
    },
    {
      step: 3,
      title: "📋 Take Your Exam",
      description: "Start your secure exam with built-in monitoring",
      action: "View My Exams",
      link: "/student-dashboard",
      time: "As needed",
      details: [
        "Click on assigned exam",
        "Read exam instructions carefully",
        "Enable camera and screen sharing",
        "Complete exam securely"
      ]
    },
    {
      step: 4,
      title: "📊 View Results",
      description: "Check your grades and feedback from teacher",
      action: "Check Results",
      link: "/student-dashboard",
      time: "Ongoing",
      details: [
        "View exam results instantly",
        "See detailed performance analytics",
        "Review incorrect answers",
        "Track your progress over time"
      ]
    }
  ];

  const quickStart = [
    {
      title: "🎥 Watch Demo (3 min)",
      description: "See ExamLock in action with our quick demo",
      link: "/demo",
      color: "bg-blue-500"
    },
    {
      title: "📞 Book Demo Call",
      description: "Get personalized walkthrough from our team", 
      link: "/contact",
      color: "bg-green-500"
    },
    {
      title: "📚 Read Documentation",
      description: "Comprehensive guides and tutorials",
      link: "/help",
      color: "bg-purple-500"
    },
    {
      title: "💬 Live Chat Support",
      description: "Get instant help from our support team",
      link: "/contact",
      color: "bg-orange-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">
              🚀 Get Started with ExamLock
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Set up your secure testing environment in under 10 minutes. 
              Follow our step-by-step guide to start creating and taking exams today.
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <button
                onClick={() => setUserType('teacher')}
                className={`p-6 rounded-xl border-2 transition duration-200 ${
                  userType === 'teacher' 
                    ? 'bg-white text-blue-600 border-white' 
                    : 'bg-white bg-opacity-20 border-white border-opacity-50 hover:bg-opacity-30'
                }`}
              >
                <div className="text-4xl mb-3">👩‍🏫</div>
                <div className="text-xl font-bold">I'm a Teacher</div>
                <div className="text-sm opacity-90">Create and manage exams</div>
              </button>
              <button
                onClick={() => setUserType('student')}
                className={`p-6 rounded-xl border-2 transition duration-200 ${
                  userType === 'student' 
                    ? 'bg-white text-blue-600 border-white' 
                    : 'bg-white bg-opacity-20 border-white border-opacity-50 hover:bg-opacity-30'
                }`}
              >
                <div className="text-4xl mb-3">👨‍🎓</div>
                <div className="text-xl font-bold">I'm a Student</div>
                <div className="text-sm opacity-90">Take secure exams</div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center">
              {steps.map((step, index) => (
                <div key={index} className="flex flex-col items-center text-center relative">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mb-3 ${
                    currentStep >= step.number ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {step.icon}
                  </div>
                  <div className="text-sm font-semibold text-gray-900">{step.title}</div>
                  <div className="text-xs text-gray-500">{step.description}</div>
                  {index < steps.length - 1 && (
                    <div className={`absolute top-6 left-12 w-24 h-0.5 ${
                      currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Teacher Path */}
      {userType === 'teacher' && (
        <div className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">👩‍🏫 Teacher Quick Start Guide</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From signup to your first secure exam in under 15 minutes
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {teacherSteps.map((step, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition duration-300">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                        {step.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{step.description}</p>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">Step-by-step:</h4>
                          <ul className="space-y-2">
                            {step.details.map((detail, detailIndex) => (
                              <li key={detailIndex} className="flex items-start text-gray-700">
                                <span className="text-green-500 mr-3 mt-0.5">✅</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500 mb-3">⏱️ Estimated time: {step.time}</div>
                          <Link
                            to={step.link}
                            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition duration-200 shadow-lg"
                          >
                            {step.action}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Student Path */}
      {userType === 'student' && (
        <div className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">👨‍🎓 Student Quick Start Guide</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Get set up and ready for secure exams in minutes
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {studentSteps.map((step, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition duration-300">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                        {step.step}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{step.description}</p>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">What to do:</h4>
                          <ul className="space-y-2">
                            {step.details.map((detail, detailIndex) => (
                              <li key={detailIndex} className="flex items-start text-gray-700">
                                <span className="text-green-500 mr-3 mt-0.5">✅</span>
                                <span>{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500 mb-3">⏱️ Takes: {step.time}</div>
                          <Link
                            to={step.link}
                            className="inline-block bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition duration-200 shadow-lg"
                          >
                            {step.action}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Start Options */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">⚡ Need Help Getting Started?</h2>
            <p className="text-xl text-gray-600">
              Multiple ways to learn and get support
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {quickStart.map((option, index) => (
              <Link
                key={index}
                to={option.link}
                className={`${option.color} text-white p-6 rounded-xl hover:opacity-90 transition duration-200 text-center block`}
              >
                <h3 className="text-lg font-bold mb-3">{option.title}</h3>
                <p className="text-sm opacity-90">{option.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">❓ Quick Start FAQs</h2>
            <p className="text-xl text-gray-600">Common questions for new users</p>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">How long does setup take?</h3>
              <p className="text-gray-700">Teachers can create their first exam in under 15 minutes. Students can join and start taking exams in under 5 minutes.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Do I need special software?</h3>
              <p className="text-gray-700">No downloads required! ExamLock works in any modern web browser on computers, tablets, and smartphones.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Is the free trial really free?</h3>
              <p className="text-gray-700">Yes! No credit card required. Teachers get 5 days to test all features with up to 5 students completely free.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <h3 className="text-xl font-bold text-gray-900 mb-3">What if I need help?</h3>
              <p className="text-gray-700">Our support team responds within 24 hours via email, or use live chat for instant help during business hours.</p>
            </div>
          </div>
        </div>
      </div>


      {/* Final CTA */}
      <div className="py-20 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">🎯 Ready to Get Started?</h2>
            <p className="text-xl text-green-100 mb-8">
              Join 500+ teachers and 50,000+ students already using ExamLock for secure,
              AI-powered testing. Start your journey today!
            </p>
            <div className="space-y-4">
              <Link
                to="/teacher-register"
                className="inline-block bg-white text-blue-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-blue-50 transition duration-200 shadow-lg mx-2"
              >
                👩‍🏫 Start as Teacher
              </Link>
              <Link
                to="/student-login"
                className="inline-block bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition duration-200 mx-2"
              >
                👨‍🎓 Student Login
              </Link>
            </div>
            <p className="text-green-200 text-sm mt-6">
              ✨ Free trial • No credit card required • Full support included
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}